import { useAtom, useAtomValue } from 'jotai'
import { atomWithStorage, RESET } from 'jotai/utils'
import { useCallback, useMemo } from 'react'

import type { SwitchboardFlowData } from '../converters/shared/reactFlowTypes'

/**
 * Storage key for the current switchboard draft.
 * Only one draft is stored at a time - switching switchboards clears the previous draft.
 */
const DRAFT_STORAGE_KEY = 'switchboard-draft-v2'

/**
 * Draft data structure stored in localStorage.
 * Contains the switchboard ID to validate the draft belongs to the current switchboard.
 */
export interface SwitchboardDraft {
  /** The switchboard ID this draft belongs to */
  switchboardId: number
  /** The flow data (nodes and edges) */
  flowData: SwitchboardFlowData
  /** Timestamp when the draft was last saved */
  lastSaved: number
  /** Server updated_at timestamp this draft was based on (if known) */
  serverUpdatedAt?: string
}

function persistDraftToStorage(
  switchboardId: number,
  flowData: SwitchboardFlowData,
  serverUpdatedAt?: string,
): void {
  if (typeof window === 'undefined') return

  try {
    const draft: SwitchboardDraft = {
      switchboardId,
      flowData,
      lastSaved: Date.now(),
      serverUpdatedAt,
    }
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
  } catch (e) {
    console.error('Failed to save switchboard draft to localStorage:', e)
  }
}

function removeDraftFromStorage(): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY)
  } catch (e) {
    console.error('Failed to remove switchboard draft from localStorage:', e)
  }
}

function parseTimestamp(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }

  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}

function isServerNewerThanDraft(
  draft: SwitchboardDraft,
  serverUpdatedAt?: string,
): boolean {
  const serverMs = parseTimestamp(serverUpdatedAt)
  if (serverMs === null) return false

  const draftServerMs = parseTimestamp(draft.serverUpdatedAt)
  if (draftServerMs !== null) {
    return serverMs > draftServerMs
  }

  // Backward compatibility: old drafts do not include serverUpdatedAt.
  if (Number.isFinite(draft.lastSaved)) {
    return serverMs > draft.lastSaved
  }

  return false
}

/**
 * Jotai atom with localStorage persistence for switchboard drafts.
 * Uses atomWithStorage to automatically sync with localStorage.
 */
const switchboardDraftAtom = atomWithStorage<SwitchboardDraft | null>(
  DRAFT_STORAGE_KEY,
  null,
)

/**
 * @param switchboardId - The ID of the current switchboard
 * @returns Object with draft data and functions to save/clear the draft
 */
export function useSwitchboardDraft(
  switchboardId: number,
  serverUpdatedAt?: string,
) {
  const [draftState, setDraftState] = useAtom(switchboardDraftAtom)

  // Get draft only if it belongs to the current switchboard
  const draft = useMemo(() => {
    if (!draftState) return null
    if (draftState.switchboardId !== switchboardId) {
      // Draft is for a different switchboard - treat as no draft
      return null
    }
    if (isServerNewerThanDraft(draftState, serverUpdatedAt)) {
      return null
    }
    // Validate draft has actual data
    if (
      draftState.flowData &&
      Array.isArray(draftState.flowData.nodes) &&
      Array.isArray(draftState.flowData.edges) &&
      (draftState.flowData.nodes.length > 0 ||
        draftState.flowData.edges.length > 0)
    ) {
      return draftState.flowData
    }
    return null
  }, [draftState, switchboardId, serverUpdatedAt])

  const saveDraft = useCallback(
    (flowData: SwitchboardFlowData, serverUpdatedAtOverride?: string) => {
      const effectiveServerUpdatedAt =
        serverUpdatedAtOverride ?? serverUpdatedAt
      // Persist synchronously first so a fast refresh/unload still keeps edits.
      persistDraftToStorage(switchboardId, flowData, effectiveServerUpdatedAt)
      setDraftState({
        switchboardId,
        flowData,
        lastSaved: Date.now(),
        serverUpdatedAt: effectiveServerUpdatedAt,
      })
    },
    [switchboardId, serverUpdatedAt, setDraftState],
  )

  const clearDraft = useCallback(() => {
    // Remove synchronously so immediate unload/refresh cannot resurrect stale draft.
    removeDraftFromStorage()
    setDraftState(RESET)
  }, [setDraftState])

  // Check if draft exists for this switchboard
  const hasDraft = draft !== null

  return {
    /** The draft flow data, or null if no valid draft exists */
    draft,
    /** Save flow data as draft */
    saveDraft,
    /** Clear the draft from storage */
    clearDraft,
    /** Whether a valid draft exists for this switchboard */
    hasDraft,
  }
}

/**
 * Hook to read draft state without write capabilities.
 * Useful for components that only need to check if a draft exists.
 *
 * @param switchboardId - The ID of the switchboard to check
 * @returns The draft flow data, or null if no valid draft exists
 */
export function useSwitchboardDraftValue(
  switchboardId: number,
  serverUpdatedAt?: string,
): SwitchboardFlowData | null {
  const draftState = useAtomValue(switchboardDraftAtom)

  return useMemo(() => {
    if (!draftState) return null
    if (draftState.switchboardId !== switchboardId) return null
    if (isServerNewerThanDraft(draftState, serverUpdatedAt)) return null
    if (
      draftState.flowData &&
      Array.isArray(draftState.flowData.nodes) &&
      Array.isArray(draftState.flowData.edges) &&
      (draftState.flowData.nodes.length > 0 ||
        draftState.flowData.edges.length > 0)
    ) {
      return draftState.flowData
    }
    return null
  }, [draftState, switchboardId, serverUpdatedAt])
}

/**
 * Hook to check if any switchboard has a draft.
 * Useful for showing indicators in switchboard lists.
 *
 * @param switchboardId - The ID of the switchboard to check
 * @returns true if a valid draft exists for this switchboard
 */
export function useHasSwitchboardDraft(switchboardId: number): boolean {
  const draft = useSwitchboardDraftValue(switchboardId)
  return draft !== null
}

// =============================================================================
// Legacy synchronous functions (for backward compatibility)
// These are kept for cases where hooks cannot be used (e.g., initial state)
// Prefer using the hooks above when possible.
// =============================================================================

/**
 * Get the current draft from localStorage (synchronous).
 * Returns null if no draft exists or if the draft is for a different switchboard.
 *
 * @param switchboardId - The ID of the current switchboard
 * @returns The flow data if a valid draft exists, null otherwise
 */
export function getDraftFromStorage(
  switchboardId: number,
  serverUpdatedAt?: string,
): SwitchboardFlowData | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY)
    if (!stored) return null

    const draft = JSON.parse(stored) as SwitchboardDraft

    // If draft is for a different switchboard, return null (don't clear - let hook handle it)
    if (draft.switchboardId !== switchboardId) {
      return null
    }
    if (isServerNewerThanDraft(draft, serverUpdatedAt)) {
      return null
    }

    // Validate draft has actual data with proper array checks
    if (
      draft.flowData &&
      Array.isArray(draft.flowData.nodes) &&
      Array.isArray(draft.flowData.edges) &&
      (draft.flowData.nodes.length > 0 || draft.flowData.edges.length > 0)
    ) {
      return draft.flowData
    }
  } catch (e) {
    console.error('Failed to read switchboard draft from localStorage:', e)
  }
  return null
}

/**
 * Get the current draft plus its metadata from localStorage (synchronous).
 * Returns null if no draft exists, if it is for a different switchboard, if the
 * server is newer, or if the draft has no data.
 *
 * Unlike {@link getDraftFromStorage}, this exposes `lastSaved` so callers can
 * compare draft freshness against a saved post-save snapshot.
 *
 * @param switchboardId - The ID of the current switchboard
 * @param serverUpdatedAt - The server's `updated_at`, used to discard stale drafts
 */
export function getDraftWithMetaFromStorage(
  switchboardId: number,
  serverUpdatedAt?: string,
): { flowData: SwitchboardFlowData; lastSaved?: number } | null {
  if (typeof window === 'undefined') return null

  try {
    const stored = localStorage.getItem(DRAFT_STORAGE_KEY)
    if (!stored) return null

    const draft = JSON.parse(stored) as SwitchboardDraft

    if (draft.switchboardId !== switchboardId) {
      return null
    }
    if (isServerNewerThanDraft(draft, serverUpdatedAt)) {
      return null
    }

    if (
      draft.flowData &&
      Array.isArray(draft.flowData.nodes) &&
      Array.isArray(draft.flowData.edges) &&
      (draft.flowData.nodes.length > 0 || draft.flowData.edges.length > 0)
    ) {
      return {
        flowData: draft.flowData,
        lastSaved: Number.isFinite(draft.lastSaved)
          ? draft.lastSaved
          : undefined,
      }
    }
  } catch (e) {
    console.error('Failed to read switchboard draft from localStorage:', e)
  }
  return null
}

/**
 * Save a draft to localStorage (synchronous).
 *
 * @deprecated Prefer useSwitchboardDraft hook for reactive updates
 * @param switchboardId - The ID of the current switchboard
 * @param flowData - The flow data to save
 */
export function saveDraftToStorage(
  switchboardId: number,
  flowData: SwitchboardFlowData,
  serverUpdatedAt?: string,
): void {
  persistDraftToStorage(switchboardId, flowData, serverUpdatedAt)
}

/**
 * Clear the draft from localStorage.
 *
 */
export function clearDraftFromStorage(): void {
  if (typeof window === 'undefined') return

  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY)
  } catch (e) {
    console.error('Failed to clear switchboard draft from localStorage:', e)
  }
}

/**
 * Check if there's a valid draft for the given switchboard.
 *
 * @param switchboardId - The ID of the switchboard to check
 * @returns true if a valid draft exists for this switchboard
 */
export function hasDraftInStorage(
  switchboardId: number,
  serverUpdatedAt?: string,
): boolean {
  return getDraftFromStorage(switchboardId, serverUpdatedAt) !== null
}
